<template>
  <v-card class="analysis-card card-standard">
    <v-card-title class="typography-h5 weight-bold">
      {{ $t('analysis.results.title') }}
    </v-card-title>
    <v-card-text class="analysis-container">
      <template v-if="analysisCategories.length > 0">
        <v-card class="quick-read-card card-soft mb-4">
          <v-card-item>
            <div class="text-overline text-primary mb-2">
              {{ $t('analysis.results.quickRead.title') }}
            </div>
            <v-row>
              <v-col cols="12" md="4">
                <div class="quick-read-section">
                  <div class="quick-read-label">{{ $t('analysis.results.quickRead.strengths') }}</div>
                  <div class="quick-read-list">
                    <v-chip
                      v-for="item in quickRead.strongest"
                      :key="`strong-${item.name}`"
                      size="small"
                      color="success"
                      variant="tonal"
                    >
                      {{ $t(`analysis.results.categories.${item.name}`) }}
                    </v-chip>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="quick-read-section">
                  <div class="quick-read-label">{{ $t('analysis.results.quickRead.watchouts') }}</div>
                  <div class="quick-read-list">
                    <v-chip
                      v-for="item in quickRead.watchouts"
                      :key="`watch-${item.name}`"
                      size="small"
                      color="warning"
                      variant="tonal"
                    >
                      {{ $t(`analysis.results.categories.${item.name}`) }}
                    </v-chip>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="quick-read-section">
                  <div class="quick-read-label">{{ $t('analysis.results.quickRead.nextMoves') }}</div>
                  <ul class="quick-read-actions">
                    <li
                      v-for="suggestion in quickRead.prioritySuggestions"
                      :key="suggestion.title"
                    >
                      <span class="quick-read-action-text">{{ suggestion.title }}</span>
                      <button
                        type="button"
                        class="quick-read-studio-link"
                        :data-testid="'open-in-studio-' + (suggestion.category || 'next-move')"
                        @click="openInStudio(suggestion)"
                      >
                        <v-icon icon="mdi-auto-fix" size="14" />
                        {{ $t('analysis.results.quickRead.openInStudio') }}
                      </button>
                    </li>
                  </ul>
                </div>
              </v-col>
            </v-row>
          </v-card-item>
        </v-card>

        <!-- FloorplanPreview and Spider Chart side by side -->
        <v-row class="mb-4">
          <v-col cols="12" sm="12" md="6">
            <FloorplanPreview />
          </v-col>
          <v-col cols="12" sm="12" md="6" class="d-none d-sm-flex">
            <v-card class="card-standard h-100">
              <v-card-item class="d-flex justify-center align-center h-100">
                <SpiderChart :labels="radarLabels" :scores="radarScores" />
              </v-card-item>
            </v-card>
          </v-col>
        </v-row>

        <!-- General Impression -->
        <v-row class="mb-4" v-if="analysis.genereller_eindruck">
          <v-col cols="12">
            <v-card class="card-standard">
              <v-card-item>
                <div class="d-flex align-center mb-4">
                  <v-icon icon="mdi-home-search" color="primary" class="analysis-icon"></v-icon>
                  <h2 class="typography-h6 weight-semibold">
                    {{ $t('analysis.results.generalImpression') }}
                  </h2>
                </div>
                <div
                  class="typography-body text-secondary analysis-description text-wrap general-impression-body"
                  :class="{ 'is-collapsed': isImpressionCollapsed }"
                  v-html="formatMarkdownLite(analysis.genereller_eindruck)"
                ></div>
                <button
                  type="button"
                  class="mobile-collapse-toggle d-sm-none"
                  @click="isImpressionCollapsed = !isImpressionCollapsed"
                >
                  {{ isImpressionCollapsed ? $t('common.showMore') : $t('common.showLess') }}
                </button>
              </v-card-item>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col 
            v-for="category in analysisCategories"
            :key="category.name"
            cols="12"
            sm="12"
            md="6"
            class="d-flex"
          >
            <v-card class="category-card card-standard flex-grow-1">
              <v-card-item>
                <!-- Category Header -->
                <div class="d-flex align-center justify-space-between mb-4">
                  <div class="d-flex align-center">
                    <v-icon :icon="getIconForCategory(category.name)" color="primary" class="analysis-icon"></v-icon>
                    <h3 class="typography-h6 weight-semibold">{{ $t(`analysis.results.categories.${category.name}`) }}</h3>
                  </div>
                  <v-rating
                    :model-value="calculateCategoryScore(category)"
                    readonly
                    density="compact"
                    color="primary"
                    length="5"
                  ></v-rating>
                </div>

                <!-- Cross-product affordance per v2 spec §14: when the
                  category score is ≤ 3, offer a one-click jump into
                  Studio pre-filled with a category-specific directive. -->
                <div
                  v-if="shouldShowStudioLink(category)"
                  class="category-studio-link"
                >
                  <button
                    type="button"
                    class="quick-read-studio-link"
                    :data-testid="`studio-link-category-${category.name}`"
                    @click="openCategoryInStudio(category)"
                  >
                    <v-icon icon="mdi-auto-fix" size="14" />
                    {{ $t('analysis.results.quickRead.openInStudio') }}
                  </button>
                </div>

                <!-- Category Description -->
                <div class="mb-4">
                  <PremiumContent 
                    :is-premium="isPremiumCategory(category.name)"
                    :preview-content="category.description.substring(0, 0)"
                    @upgrade="navigateToPricing"
                  >
                    <span
                      class="typography-body text-secondary analysis-description text-wrap"
                      v-html="formatMarkdownLite(category.description)"
                    ></span>
                  </PremiumContent>
                </div>

                <!-- Category Aspects -->
                <v-list>
                  <v-list-item
                    v-for="(aspect, key) in category.aspects"
                    :key="key"
                    class="analysis-item mb-4"
                    :class="{ 'premium-item': isPremiumCategory(category.name) }"
                    @click="isPremiumCategory(category.name) && handlePremiumContent(category.name)"
                  >
                    <div>
                      <v-list-item-title class="typography-body weight-semibold mb-2">
                        {{ $t(`analysis.results.aspects.${key}`) }}
                        <v-chip
                          size="small"
                          class="ml-2 rating-chip"
                          :class="getRatingClass(aspect.rating)"
                        >
                          {{ $t(`analysis.results.ratings.${aspect.rating}`) }}
                        </v-chip>
                      </v-list-item-title>
                      <PremiumContent 
                        :is-premium="isPremiumCategory(category.name)"
                        :preview-content="aspect.description.substring(0, 0)"
                        @upgrade="navigateToPricing"
                      >
                        <span class="typography-body text-secondary analysis-description text-wrap" 
                              v-html="formatMarkdownLite(aspect.description)">
                        </span>
                      </PremiumContent>
                    </div>
                  </v-list-item>
                </v-list>
              </v-card-item>
            </v-card>
          </v-col>
        </v-row>
      </template>
      
      <div v-else class="text-center pa-4">
        {{ $t('analysis.results.noResults') }}
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import PremiumContent from '@/components/common/PremiumContent.vue'
import FloorplanPreview from '@/components/FloorplanPreview.vue'
import SpiderChart from '@/components/analysis/SpiderChart.vue'
import { trackPremiumUpsellClick, trackUnlockCtaClicked } from '@/utils/analytics'
import { formatMarkdownLite } from '@/utils/textFormatting'
import { scrollToUnlockPanel } from '@/utils/scrollToUnlockPanel'
import { buildUnlockCtaPayload } from '@/utils/unlockTracking.mjs'

// i18n
const { t: $t } = useI18n()

const store = useStore()
const route = useRoute()
const router = useRouter()
const analysis = computed(() => store.state.analysis?.analysis || {})

const analysisRouteId = computed(() => String(route.params.id || store.state.analysis?.analysisId || 'current'))

const openInStudio = (suggestion) => {
  if (!suggestion) return
  const prefill = suggestion.title || ''
  router.push({
    name: 'AnalysisStudio',
    params: { id: analysisRouteId.value },
    query: { prefill, source: 'analysis_next_move' },
  })
}

// Per v2 spec §14, every scored category with a score ≤ 3 surfaces a
// "Im Studio iterieren →" chip with a category-specific directive.
const CATEGORY_PROMPT_TEMPLATES = {
  raumfluss: 'Raumfluss verbessern: Engstellen entschärfen und Wege zwischen Eingang, Küche und Wohnbereich straffen.',
  lichteinfall: 'Tageslicht dort einplanen, wo der Grundriss am dunkelsten ist.',
  stauraum: 'Stauraum in ungenutzten Ecken und Eingangsbereichen ergänzen.',
  raumaufteilung: 'Raumaufteilung überdenken: Zonen klarer trennen oder zusammenführen.',
  funktionalitaet: 'Funktionalität schärfen: Türanschläge, Bewegungsradien und Ergonomie der Engstellen prüfen.',
  privatsphaere: 'Privatsphäre stärken: Sichtachsen reduzieren und Schlafzone vom Eingang puffern.',
  zugaenglichkeit: 'Zugänglichkeit verbessern: Türbreiten, Schwellen und Bewegungsflächen an Barrierefreiheit ausrichten.',
  flexibilitaet: 'Flexibilität erhöhen: Räume so anlegen, dass sie für unterschiedliche Lebensphasen taugen.',
  aussenbezug: 'Außenbezug stärken: Sichtachsen nach draußen und Übergänge zu Balkon/Garten betonen.',
}

const shouldShowStudioLink = (category) => {
  const score = calculateCategoryScore(category)
  return Number.isFinite(score) && score > 0 && score <= 3
}

const openCategoryInStudio = (category) => {
  if (!category) return
  const prefill = CATEGORY_PROMPT_TEMPLATES[category.name]
    || $t(`analysis.results.categories.${category.name}`)
  router.push({
    name: 'AnalysisStudio',
    params: { id: analysisRouteId.value },
    query: {
      prefill,
      source: `category_${category.name}`,
      score: String(calculateCategoryScore(category)),
    },
  })
}

// Genereller Eindruck is often 800+ words. On phones we clamp it to a few
// lines and let the user expand inline (CSS handles the desktop case via
// `d-sm-none` on the toggle).
const isImpressionCollapsed = ref(true)

// Compute whether user has access to premium content
const hasPremiumAccess = computed(() => store.state.userHadPremiumWhenUploaded)

// Update the isPremiumCategory function to use the new logic
const isPremiumCategory = (category) => {
  if (hasPremiumAccess.value) return false
  return PREMIUM_CATEGORIES.includes(category)
}

// Calculate the average score for a category based on its aspects
const calculateCategoryScore = (category) => {
  if (!category.aspects || Object.keys(category.aspects).length === 0) return 0
  
  const ratings = Object.values(category.aspects).map(aspect => {
    // Convert text ratings to numeric scores (1-5 scale)
    switch(aspect.rating.toLowerCase()) {
      case 'positive': return 5
      case 'positiv': return 5
      case 'neutral': return 3      
      case 'negative': return 1
      case 'negativ': return 1
      default: return 0
    }
  })
  
  const average = ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length
  return Math.round(average * 2) / 2
}

const analysisCategories = computed(() => {
  const categories = [
    'raumaufteilung',
    'raumfluss',
    'lichteinfall',
    'funktionalitaet',
    'stauraum',
    'privatsphaere',
    'zugaenglichkeit',
    'flexibilitaet',
    'aussenbezug'
  ]
  
  return categories
    .filter(cat => analysis.value[cat])
    .map(cat => {
      const category = analysis.value[cat]
      const aspects = {}
      
      Object.entries(category).forEach(([key, value]) => {
        if (key !== 'description' && typeof value === 'object') {
          aspects[key] = {
            ...value,
            criteria: extractCriteriaFromDescription(value.description)
          }
        }
      })
      
      return {
        name: cat,
        description: category.description,
        aspects
      }
    })
})

// Data for SpiderChart
// the category name should be fetched from i18n, like $t(`analysis.results.categories.${category.name}`)
const radarLabels = computed(() => analysisCategories.value.map(c => $t(`analysis.results.categories.${c.name}`)))
const radarScores = computed(() => analysisCategories.value.map(c => calculateCategoryScore(c)))
const quickRead = computed(() => {
  const sortedCategories = analysisCategories.value
    .map((category) => ({
      name: category.name,
      score: calculateCategoryScore(category),
    }))
    .sort((left, right) => right.score - left.score)

  return {
    strongest: sortedCategories.slice(0, 2),
    watchouts: [...sortedCategories].reverse().slice(0, 2),
    prioritySuggestions: (analysis.value?.suggestions || []).slice(0, 2),
  }
})

const extractCriteriaFromDescription = (description) => {
  if (!description) return ''
  const parts = description.split('\n\n')
  return parts[1] || ''
}

const getIconForCategory = (category) => {
  const categoryIcons = {
    raumaufteilung: 'mdi-floor-plan',
    raumfluss: 'mdi-routes',
    lichteinfall: 'mdi-lightbulb',
    funktionalitaet: 'mdi-lightning-bolt',
    stauraum: 'mdi-package-variant-closed',
    privatsphaere: 'mdi-shield-account',
    zugaenglichkeit: 'mdi-account-outline',
    flexibilitaet: 'mdi-sync',
    aussenbezug: 'mdi-nature',
    default: 'mdi-home'
  }
  return categoryIcons[category] || categoryIcons.default
}

const getRatingClass = (rating) => {
  const classes = {
    positive: 'rating-positive',
    positiv: 'rating-positive',
    neutral: 'rating-neutral',
    negative: 'rating-negative',
    negativ: 'rating-negative'
  }
  return classes[rating] || 'rating-neutral'
}

// Add this constant for premium categories
const PREMIUM_CATEGORIES = [
  'funktionalitaet',
  'stauraum',
  'privatsphaere',
  'zugaenglichkeit',
  'flexibilitaet',
  'aussenbezug',
  'raumfluss',
  'lichteinfall',
]

// Keep these functions as they're still needed:
const handlePremiumContent = (category) => {
  trackPremiumUpsellClick(category)

  trackUnlockCtaClicked(buildUnlockCtaPayload({
    source: 'analysis_results',
    userIntent: store.state.userIntent,
    extra: {
      category,
    },
  }))
  
  if (!hasPremiumAccess.value) {
    navigateToPricing()
  }
}

const navigateToPricing = () => {
  scrollToUnlockPanel()
}
</script>

<style scoped>
.analysis-container {
  max-height: none;
  overflow-y: visible;
}

.quick-read-card {
}

.quick-read-label {
  font-size: var(--font-size-caption);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-secondary);
  margin-bottom: 10px;
}

.quick-read-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.quick-read-actions {
  margin: 0;
  padding-left: 18px;
  color: var(--text-primary);
}

.quick-read-actions li + li {
  margin-top: 6px;
}

.quick-read-actions li {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: baseline;
  gap: 8px;
}

/* On mobile the "Im Studio iterieren →" pill (~140px nowrap) was eating
   most of the row, leaving 6-char wide column that broke "Erschließungs-
   achse" into vertical fragments. Stack the text + the pill so the
   suggestion stays fully readable. */
@media (max-width: 600px) {
  .quick-read-actions li {
    grid-template-columns: minmax(0, 1fr);
    align-items: stretch;
    gap: 6px;
  }
  .quick-read-studio-link {
    justify-self: start;
  }
}

.quick-read-action-text {
  min-width: 0;
}

.quick-read-studio-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(var(--v-theme-primary), 0.1);
  color: rgb(var(--v-theme-primary));
  font-size: var(--font-size-caption);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.quick-read-studio-link:hover {
  background: rgba(var(--v-theme-primary), 0.18);
}

.category-card {
  height: 100%;
}

.category-studio-link {
  margin-bottom: 12px;
  display: flex;
  justify-content: flex-end;
}

.analysis-item {
  margin-bottom: 16px;
  transition: all 0.3s ease;
  padding: 2px;
  border-radius: 4px;
}

.analysis-item:hover {
  background-color: var(--background);
}

.analysis-icon {
  margin-right: 12px;
  font-size: 24px;
}

.analysis-description {
  white-space: normal !important;
  overflow: visible;
  text-overflow: unset;
  word-wrap: break-word;
}

.rating-chip {
  background-color: rgba(15, 23, 42, 0.08) !important;
  color: var(--text-primary) !important;
  font-weight: var(--font-weight-medium);
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.12);
}

.rating-chip.rating-positive,
.rating-chip.rating-negative,
.rating-chip.rating-neutral {
  border-color: rgba(15, 23, 42, 0.2);
}

.category-description {
  font-style: italic;
  padding: 8px 0;
}

/* Premium content styles */
.premium-item {
  position: relative;
}

.premium-message {
  display: none;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: var(--text-primary);
  font-weight: var(--font-weight-semibold);
  text-align: center;
  padding: 4px 8px;
  font-size: var(--font-size-body-2);
  background: rgba(255, 255, 255, 0.95);
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  pointer-events: auto;
  z-index: 1;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.premium-item:hover .premium-message {
  display: flex;
}

/* Responsive grid spacing */
:deep(.v-row) {
  margin: 0 -8px;
}

:deep(.v-col) {
  padding: 8px;
}

/* Card content padding */
:deep(.v-card-item) {
  padding: 14px;
}

.general-impression-text {
  white-space: normal;
  line-height: 1.6;
}

/* Remove custom typography styles and let Vuetify handle it */
:deep(.v-list-item-subtitle) {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
  -webkit-line-clamp: unset !important;
}

/* Update typography classes */
.analysis-container {
  max-height: none;
  overflow-y: visible;
}

/* Remove any custom typography styles and let Vuetify handle it */
:deep(.v-card-title) {
  font-size: inherit;
  font-weight: inherit;
}

/* Ensure heading elements work within flex layouts */
h2.typography-h6,
h3.typography-h6 {
  margin: 0;
}

/* Collapse the long Genereller Eindruck prose on phones with an inline
   "Mehr anzeigen" toggle. The toggle button itself only renders on xs
   (Vuetify's d-sm-none). */
.mobile-collapse-toggle {
  display: inline-block;
  margin-top: 8px;
  padding: 0;
  background: none;
  border: 0;
  color: rgb(var(--v-theme-primary));
  font: inherit;
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.mobile-collapse-toggle:hover,
.mobile-collapse-toggle:focus-visible {
  text-decoration-thickness: 2px;
  outline: none;
}

@media (max-width: 600px) {
  .general-impression-body.is-collapsed {
    display: -webkit-box;
    -webkit-line-clamp: 6;
    -webkit-box-orient: vertical;
    overflow: hidden;
    /* Leave room for the toggle button below */
    margin-bottom: 0;
  }
}
</style>
